import React, { useEffect, useCallback, useRef } from 'react';
import useDebounce from '../../utils/useDebounce';
import { PageHeader } from '../../components';
import styles from './AddProducts.module.scss';
import { Button, Col, Form, Row, Card, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
    PRODUCT_FORM_FIELDS as FORM_FIELDS,
    INITIAL_FORM_DATA,
    buildPayload,
    validateForm,
    DEBOUNCE_DELAY
} from './utils/AddProductsConstants';
import { connect } from 'react-redux';
import { getVoucherThemes } from '../../api/voucherTheme';
import { getAllMerchantGroupApi, getMerchantGroupsBySearch } from '../../api/merchant';
import { getAddOns } from '../../api/Vouchers';
import { addProduct, getProductCategories } from '../../api/Products';
import CategorySelect from './utils/CategorySelect';
import VoucherThemeSelect from './utils/VoucherThemeSelect';
import AddOnsSelect from './utils/AddOnsSelect';
import MerchantGroupSelect from './utils/MerchantGroupSelect';
import OptionsSection from './utils/OptionsSection';
import useComponentState from '../../utils/useComponentState';


const AddProducts = ({ auth }) => {
    const [state, setState] = useComponentState({
        formData: { ...INITIAL_FORM_DATA },
        categories: [],
        voucherThemes: [],
        addOns: [],
        merchantGroups: [],
        loadingMerchantGroups: false,
        loadingVoucherThemes: false,
        loadingCategories: false,
        loadingAddOns: false,
        merchantGroupSearchTerm: '',
        searchType: 'ID',
        validated: false,
        error: '',
        loading: false,
        fieldErrors: {}
    });

    // Track latest request and allow aborting stale requests
    const latestReqIdRef = useRef(0);
    const abortCtrlRef = useRef(null);

    // Debounce the merchant group search term coming from the UI
    const debouncedMerchantGroupSearchTerm = useDebounce(state.merchantGroupSearchTerm, DEBOUNCE_DELAY);

    // Fetch product categories
    const fetchCategories = useCallback(async () => {
        setState(prevState => ({ ...prevState, loadingCategories: true }));
        try {
            const response = await getProductCategories(auth.auth.token);
            if (response) {
                setState(prevState => ({ ...prevState, categories: response }));
            }
        } catch (error) {
            toast.error('Failed to fetch categories');
        } finally {
            setState(prevState => ({ ...prevState, loadingCategories: false }));
        }
    }, [auth.auth.token]);

    // Fetch voucher themes
    const fetchVoucherThemes = useCallback(async () => {
        setState(prevState => ({ ...prevState, loadingVoucherThemes: true }));
        try {
            const response = await getVoucherThemes(auth.auth.token, 0, 100);
            if (response && response.list && response.list.length > 0) {
                const flatThemes = response.list
                    .filter(group => Array.isArray(group.voucherThemes))
                    .flatMap(group => group.voucherThemes);
                setState(prevState => ({ ...prevState, voucherThemes: flatThemes }));
            }
        } catch (error) {
            toast.error('Failed to fetch voucher themes');
        } finally {
            setState(prevState => ({ ...prevState, loadingVoucherThemes: false }));
        }
    }, [auth.auth.token]);

    // Fetch add-ons
    const fetchAddOns = useCallback(async () => {
        setState(prevState => ({ ...prevState, loadingAddOns: true }));
        try {
            const response = await getAddOns(auth.auth.token);
            if (response) {
                setState(prevState => ({ ...prevState, addOns: response }));
            }
        } catch (error) {
            toast.error('Failed to fetch add-ons');
        } finally {
            setState(prevState => ({ ...prevState, loadingAddOns: false }));
        }
    }, [auth.auth.token]);

    // Fetch merchant groups
    const fetchMerchantGroups = useCallback(async () => {
        setState(prevState => ({ ...prevState, loadingMerchantGroups: true }));
        try {
            const response = await getAllMerchantGroupApi(auth.auth.token, 1, 100, 'name', 'asc');

            if (response && response.list) {
                setState(prevState => ({ ...prevState, merchantGroups: response.list }));
            }
        } catch (error) {
            toast.error('Failed to fetch merchant groups');
        } finally {
            setState(prevState => ({ ...prevState, loadingMerchantGroups: false }));
        }
    }, [auth.auth.token]);

    const fetchMerchantGroupsWithSearch = useCallback(async (term, searchType, token) => {
        // abort previous
        if (abortCtrlRef.current) abortCtrlRef.current.abort();

        const reqId = ++latestReqIdRef.current;
        const controller = new AbortController();
        abortCtrlRef.current = controller;

        setState(prev => ({ ...prev, loadingMerchantGroups: true }));

        try {
            let response;

            if (!term) {
                // No search term → fetch all merchant groups
                response = await getAllMerchantGroupApi(token, 1, 100, 'name', 'asc', { signal: controller.signal });
                response = response?.list || response || [];
            } else {
                // With search term → perform filtered search
                response = await getMerchantGroupsBySearch(token, term, (searchType || 'ID').toUpperCase(), { signal: controller.signal });
            }

            // ignore stale responses
            if (reqId !== latestReqIdRef.current) return;

            setState(prev => ({ ...prev, merchantGroups: response, loadingMerchantGroups: false }));
        } catch (err) {
            if (reqId !== latestReqIdRef.current) return;
            if (err?.name === 'AbortError') return; // request cancelled

            toast.error('Failed to search merchant groups');
            setState(prev => ({ ...prev, loadingMerchantGroups: false }));
        }
    }, []);


    // Effect: fetch merchant groups when the debounced term or search type changes
    useEffect(() => {
        const term = debouncedMerchantGroupSearchTerm?.toString().trim();
        fetchMerchantGroupsWithSearch(term, state.searchType, auth.auth.token);

        // cleanup when dependency changes
        return () => {
            if (abortCtrlRef.current) abortCtrlRef.current.abort();
        };
    }, [debouncedMerchantGroupSearchTerm, state.searchType, auth.auth.token, fetchMerchantGroupsWithSearch]);


    useEffect(() => {
        fetchCategories();
        fetchVoucherThemes();
        fetchAddOns();
        fetchMerchantGroups();
    }, [fetchCategories, fetchVoucherThemes, fetchAddOns, fetchMerchantGroups]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setState(prev => ({
            ...prev,
            formData: {
                ...prev.formData,
                [name]: value
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // run JS validation
        const validationErrors = validateForm(state.formData);
        if (Object.keys(validationErrors).length > 0) {
            setState(prevState => ({
                ...prevState,
                fieldErrors: validationErrors
            }));
            e.stopPropagation();
            return;
        }

        // clear errors and proceed
        setState(prevState => ({
            ...prevState,
            fieldErrors: {}
        }));

        setState(prevState => ({ ...prevState, loading: true, error: '' }));

        try {
            const payload = buildPayload(state.formData);
            // ensure numeric conversions where necessary
            if (payload.clientId !== null && typeof payload.clientId === 'string') {
                const n = Number(payload.clientId);
                payload.clientId = Number.isNaN(n) ? null : n;
            }

            if (payload.productCategoryId !== null && typeof payload.productCategoryId === 'string') {
                const n = Number(payload.productCategoryId);
                payload.productCategoryId = Number.isNaN(n) ? null : n;
            }

            // If backend expects addOnIds as numbers, ensure that
            payload.addOnIds = Array.isArray(payload.addOnIds) ? payload.addOnIds.map(Number) : [];

            const response = await addProduct(payload, auth.auth.token);

            if (response) {
                toast.success('Product Added Successfully!');

                // Reset formData to the shared initial object so all fields (including slug) are cleared
                setState(prevState => ({
                    ...prevState,
                    formData: { ...INITIAL_FORM_DATA },
                    fieldErrors: {},
                    validated: false
                }));
            } else {
                toast.error('Failed to create product: ' + response.message);
            }

        } catch (err) {
            setState(prevState => ({ ...prevState, error: 'Failed to create product. Please try again.' }));
            toast.error('Failed to create product');
        } finally {
            setState(prevState => ({ ...prevState, loading: false }));
        }
    };

    return (
        <div className={styles.container}>
            <PageHeader text="Add Products" />

            <Card className={styles.formCard}>
                <Card.Body>
                    <Form noValidate onSubmit={handleSubmit}>
                        {state.error && (
                            <Alert variant="danger" className="mb-4">
                                {state.error}
                            </Alert>
                        )}

                        <CategorySelect
                            categories={state.categories}
                            value={state.formData.selectedCategoryId}
                            onChange={handleInputChange}
                            error={state.fieldErrors.selectedCategoryId}
                        />
                        <VoucherThemeSelect
                            voucherThemes={state.voucherThemes}
                            // single-select mode (no chips)
                            edit
                            value={state.formData.selectedVoucherThemes}
                            onChange={handleInputChange}
                            error={state.fieldErrors.selectedVoucherThemes}
                        />
                        <AddOnsSelect
                            addOns={state.addOns}
                            value={state.formData.selectedAddOns}
                            onChange={handleInputChange}
                            error={state.fieldErrors.selectedAddOns}
                        />
                        <MerchantGroupSelect
                            value={state.formData.selectedMerchantGroupId}
                            onChange={handleInputChange}
                            merchantGroups={state.merchantGroups}
                            loading={state.loadingMerchantGroups}
                            searchType={state.searchType}
                            setSearchType={(name) => setState({ searchType: name })}
                            searchTerm={state.merchantGroupSearchTerm}
                            setSearchTerm={(name) => {
                                setState({ merchantGroupSearchTerm: name });
                                // the effect watching debouncedMerchantGroupSearchTerm will handle fetching
                            }}
                            // parent handles debounced fetching
                            error={state.fieldErrors.selectedMerchantGroupId}
                        />
                        {FORM_FIELDS.map(({ name, label, type, placeholder, required, rows }) => (
                            <Form.Group as={Row} className="mb-3" key={name}>
                                <Form.Label column sm={3}>
                                    {label} {required && <span className="text-danger">*</span>}
                                </Form.Label>
                                <Col sm={9}>
                                    <Form.Control
                                        as={type === 'textarea' ? type : 'input'}
                                        type={type !== 'textarea' ? type : undefined}
                                        name={name}
                                        value={state.formData[name]}
                                        onChange={handleInputChange}
                                        placeholder={placeholder}
                                        rows={rows}
                                        isInvalid={!!state.fieldErrors[name]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {state.fieldErrors[name] || (required ? 'This field is required' : '')}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                        ))}

                        <OptionsSection
                            label="Delivery Options"
                            options={[
                                { id: 'emailDelivery', name: 'emailDelivery', label: 'Email', checked: state.formData.emailDelivery },
                                { id: 'smsDelivery', name: 'smsDelivery', label: 'SMS', checked: state.formData.smsDelivery }
                            ]}
                            onToggle={(name, checked) => handleInputChange({ target: { name, value: checked } })}
                        />


                        <OptionsSection
                            label="Product Options"
                            options={[
                                { id: 'isFeatured', name: 'isFeatured', label: 'Is this a featured product?', checked: state.formData.isFeatured },
                                { id: 'isPublished', name: 'isPublished', label: 'Do you want to publish this product in customer portal?', checked: state.formData.isPublished }
                            ]}
                            onToggle={(name, checked) => handleInputChange({ target: { name, value: checked } })}
                        />


                        <div className={styles.buttonContainer}>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={state.loading}
                            >
                                {state.loading ? 'Adding...' : 'Add Product'}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card >
        </div >
    );
}

const mapStateToProps = (state) => ({
    auth: state.auth,
});

export default connect(mapStateToProps)(AddProducts);
